import React, { useState, useEffect, useMemo, useRef } from "react";
import {
	Box,
	List,
	ListItem,
	ListItemText,
	Typography,
	Button,
	IconButton,
	Alert,
	Divider,
	Tooltip,
} from "@mui/material";
import ListPageSkeleton from "../../components/PageSkeletons/ListPageSkeleton";
import PageTitle from "../../components/PageTitle/PageTitle";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import DownloadIcon from "@mui/icons-material/Download";
import LaunchIcon from "@mui/icons-material/Launch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";
import { useSite } from "../../context/siteContext";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Datagrid from "../../components/Datagrid/Datagrid";
import DocumentViewModal from "../../components/DocumentViewModal";
import './Documents.css'
import { useTranslation } from "react-i18next";
import { ALERT_MESSAGES, TABLE_HEADERS, UI_TEXT } from "../../constants";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { getDocumentCategories, getDocuments, downloadDocument } from "../../services/documentService";
import { trackEvent } from "../../utilities/analyticsService";

const Documents = () => {
	const [loading, setLoading] = useState(true);
	const [categories, setCategories] = useState([]);
	const [documentsData, setDocumentsData] = useState({});
	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedDocuments, setSelectedDocuments] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const { selectedSite, siteData } = useSite();
	const { machine_id } = useParams();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const categoryParam = searchParams.get('category');
	const documentIdParam = searchParams.get('document_id') || location.state?.documentId || null;
	const documentNameParam = searchParams.get('document_name') || location.state?.documentName || null;
	const [selectedMachineId, setSelectedMachineId] = useState(null);
	const [selectedMachine, setSelectedMachine] = useState(null);
	const selectedCategoryRef = useRef(null);
	const notificationOpenRef = useRef(null);
	const lastTrackedSearchRef = useRef("");
	const { t } = useTranslation();

	const normalizedSearchTerm = searchTerm.trim().toLowerCase();
	const matchesDocument = (doc) => {
		if (!doc) return false;
		if (!normalizedSearchTerm) return true;
		const docName = (doc.name || "").toLowerCase();
		const categoryName = (doc.category_name || "").toLowerCase();
		return docName.includes(normalizedSearchTerm) || categoryName.includes(normalizedSearchTerm);
	};

	const visibleCategories = useMemo(() => {
		if (!normalizedSearchTerm) return categories;
		return categories.filter((category) => {
			const docs = documentsData[category.name] || [];
			const docMatches = docs.some((doc) => matchesDocument(doc));
			return category.name.toLowerCase().includes(normalizedSearchTerm) || docMatches;
		});
	}, [categories, documentsData, normalizedSearchTerm]);

	const checkAndOpenUrl = async (url) => {
		if (!url) return;
		try {
			const response = await fetch(url, { method: "HEAD" });
			if (!response.ok) {
				throw new Error(`Status ${response.status}`);
			}
			window.open(url, "_blank", "noopener,noreferrer");
		} catch (error) {
			console.error("Unable to open document url", error);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.UNABLE_TO_OPEN_DOCUMENT') || "Unable to open the document. Please try again later.",
			});
		}
	};

	// Show loader during initial fetch

	const [viewOpen, setViewOpen] = useState(false);
	const [viewDoc, setViewDoc] = useState(null);
	const navigate = useNavigate();

	useEffect(() => {
		const fetchMachineDetails = async () => {
			try {
				const response = await getMachinesByIds({ machines: [machine_id] });
				
				if (response.status) {
					setSelectedMachine(response.data[0]);
				}
			} catch (error) {
				
			}
		};
		if (machine_id) {
			fetchMachineDetails();
			setSelectedMachineId(machine_id);
		} else {
			setSelectedMachine(null);
			setSelectedMachineId(null);
		}
	}, [machine_id]);

	useEffect(() => {
		setLoading(true);
		const fetchDocuments = async () => {
			try {
				const siteId = siteData.find((i) => i.customer_site_number === selectedSite)?.site_id;
				if (!selectedSite || !siteId) {
					return;
				}

				// Fetch Categories
				const catResponse = await getDocumentCategories();
				
				let newCategories = (Array.isArray(catResponse?.data) ? catResponse.data : []).map((category) => ({
					id: category.id,
					name: category.category_name,
				}));

				// Fetch Documents
				const docResponse = await getDocuments(siteId);
				
				if (docResponse.status) {
					const databaseDocsRaw = docResponse.data;
					const databaseDocs = Array.isArray(databaseDocsRaw) ? databaseDocsRaw : [];
					const safeDocs = databaseDocs.filter((d) => d && typeof d === 'object');

					// Get site machines
					const currentSiteData = siteData.find((i) => i.customer_site_number === selectedSite);
					const siteMachines = currentSiteData?.machines || [];
					const siteMachineIds = new Set(siteMachines.map((m) => String(m?.machine_id)));

					// Limit to a single machine if routed with machine_id
					const docsFiltered = machine_id
						? safeDocs.filter(
							(doc) =>
								Array.isArray(doc?.assigned_machines) &&
								doc.assigned_machines.map(String).includes(String(machine_id))
						)
						: safeDocs;
					
					const grouped = docsFiltered.reduce((acc, doc) => {
						if (!doc) return acc;
						const categoriesForDoc = Array.isArray(doc.categories) && doc.categories.length > 0
							? doc.categories
							: [{ category_name: t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized' }];
						const type = doc.attachment_type || '';
						let path = '';
						if (type === 'file') path = doc.attachment || `Documents/${doc.name || ''}`;
						else if (type === 'url') path = doc.attachment || '';

						const assignedIdsSet = new Set(
							Array.isArray(doc?.assigned_machines)
								? doc.assigned_machines.map((id) => String(id))
								: []
						);
						let assignedSiteMachines = [];
						if (Array.isArray(doc?.machine_details) && doc.machine_details.length > 0) {
							assignedSiteMachines = doc.machine_details.filter((m) =>
								siteMachineIds.has(String(m?.machine_id))
							);
						}
						if (assignedSiteMachines.length === 0) {
							assignedSiteMachines = (siteMachines || [])
								.filter((m) => assignedIdsSet.has(String(m?.machine_id)))
								.map((m) => ({
									machine_id: m.machine_id,
									machine_name: m.machine_name,
									machine_number: m.machine_number,
								}));
						}
						if (assignedSiteMachines.length === 0 && Array.isArray(doc?.machine_details)) {
							assignedSiteMachines = doc.machine_details;
						}
						const seen = new Set();
						assignedSiteMachines = assignedSiteMachines.filter((m) => {
							const machineKey = String(m?.machine_id);
							if (seen.has(machineKey)) return false;
							seen.add(machineKey);
							return true;
						});

						const baseEntry = {
							document_id: doc.id || null,
							name: doc.name || t('UI_TEXT.UNTITLED'),
							path,
							attachment_type: type,
							description: doc.description || t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1'),
							assigned_machines: assignedSiteMachines,
							attachment: doc.attachment || null,
							visibility_status: doc.visibility_status || 'private',
						};

						categoriesForDoc.forEach((cat) => {
							const key = cat.category_name || t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized';
							if (!acc[key]) acc[key] = [];
							const entryWithCategory = {
								...baseEntry,
								id: doc.id || null,
								row_id: doc.id ? `${doc.id}-${key}` : `${baseEntry.name}-${key}-${acc[key].length}`,
								category_name: key,
							};
							acc[key].push(entryWithCategory);
						});
						return acc;
					}, {});

					const filteredCategories = newCategories.filter((cat) => grouped[cat.name]?.length > 0);
					Object.keys(grouped).forEach((key) => {
						if (!filteredCategories.some((cat) => cat.name === key)) {
							filteredCategories.push({ id: null, name: key });
						}
					});
					
					setCategories(filteredCategories);
					const preferredCategory = categoryParam
						? filteredCategories.find((cat) => cat.name.toLowerCase() === categoryParam.toLowerCase())
						: null;
					const nextCategory = preferredCategory?.name || filteredCategories[0]?.name || "";
					setSelectedCategory(nextCategory);
					setDocumentsData(grouped);
					setSelectedDocuments(grouped[nextCategory] || []);
				} else {
					console.error("Error fetching documents:", docResponse);
				}
			} catch (err) {
				console.error("Error fetching documents:", err);
			} finally {
				setLoading(false);
			}
		};

		const fetchMachineDetails = async () => {
			try {
				const response = await getMachinesByIds({ machines: [machine_id] });
				
				if (response.status) {
					setSelectedMachine(response.data[0]);
				}
			} catch (error) {
				
			}
		};
		if (machine_id) {
			fetchMachineDetails();
			setSelectedMachineId(machine_id);
			fetchDocuments();
		} else {
			fetchDocuments();
		}
	}, [selectedSite, machine_id, siteData, categoryParam]);

	const handleCategoryClick = (categoryId) => {
		setSelectedCategory(categoryId);
		setSelectedDocuments(documentsData[categoryId] || []);
		const nextParams = new URLSearchParams(searchParams);
		if (categoryId) {
			nextParams.set('category', categoryId);
		} else {
			nextParams.delete('category');
		}
		setSearchParams(nextParams, { replace: true });
	};

	const handleSearchChange = (value) => {
		setSearchTerm(value);
		const query = String(value || "").trim().toLowerCase();
		if (!query) return;
		const resultCount = (selectedDocuments || []).filter((doc) => {
			const docName = String(doc?.name || "").toLowerCase();
			const categoryName = String(doc?.category_name || "").toLowerCase();
			return docName.includes(query) || categoryName.includes(query);
		}).length;
		const trackingKey = `${query}|${selectedSite}|${selectedCategory}|${resultCount}`;
		if (lastTrackedSearchRef.current !== trackingKey) {
			lastTrackedSearchRef.current = trackingKey;
			trackEvent("search_performed", {
				search_term: value,
				search_source: "documents",
				page_name: "Documents",
				page_path: `${location.pathname}${location.search || ""}`,
				result_count: resultCount,
				has_results: resultCount > 0,
				no_results: resultCount === 0,
			});
		}
	};

	useEffect(() => {
		if (normalizedSearchTerm) return;
		if (!visibleCategories.length) return;
		const isSelectedVisible = visibleCategories.some((cat) => cat.name === selectedCategory);
		if (!isSelectedVisible) {
			const fallbackCategory = visibleCategories[0].name;
			setSelectedCategory(fallbackCategory);
			setSelectedDocuments(documentsData[fallbackCategory] || []);
			const nextParams = new URLSearchParams(searchParams);
			if (fallbackCategory) {
				nextParams.set('category', fallbackCategory);
			} else {
				nextParams.delete('category');
			}
			setSearchParams(nextParams, { replace: true });
		}
	}, [visibleCategories, selectedCategory, documentsData, searchParams, setSearchParams]);

	useEffect(() => {
		if (!selectedCategory || loading) return;

		const getScrollParent = (el) => {
			let node = el?.parentElement;
			while (node) {
				const style = window.getComputedStyle(node);
				const overflowY = style.overflowY;
				const canScroll = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
				if (canScroll) return node;
				node = node.parentElement;
			}
			return document.scrollingElement || document.documentElement;
		};

		const node = selectedCategoryRef.current;
		if (!node) return;
		const container = getScrollParent(node);
		if (!container) return;
		const containerRect = container.getBoundingClientRect();
		const itemRect = node.getBoundingClientRect();
		const itemTopWithinContainer = itemRect.top - containerRect.top;
		const targetScrollTop =
			container.scrollTop +
			itemTopWithinContainer -
			(container.clientHeight / 2 - itemRect.height / 2);

		const raf = window.requestAnimationFrame(() => {
			container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
		});
		return () => window.cancelAnimationFrame(raf);
	}, [selectedCategory, loading, visibleCategories.length, searchTerm]);

	const filteredDocuments = (selectedDocuments || [])
		.filter((doc) => matchesDocument(doc))
		.map((doc, index) => ({
			...doc,
			row_id: doc.row_id || `${doc.document_id || doc.name}-${index}`,
		}));

	useEffect(() => {
		if (loading) return;
		if (!documentIdParam && !documentNameParam) return;

		const allDocuments = Object.values(documentsData).flat();
		if (!allDocuments.length) return;

		const normalizedDocumentName = String(documentNameParam || "").trim().toLowerCase();
		const matchedDocument =
			allDocuments.find((doc) => documentIdParam && String(doc.document_id) === String(documentIdParam)) ||
			allDocuments.find((doc) => normalizedDocumentName && String(doc.name || "").trim().toLowerCase() === normalizedDocumentName);

		if (!matchedDocument) return;

		const targetCategory = matchedDocument.category_name || "";
		if (targetCategory && targetCategory !== selectedCategory) {
			setSelectedCategory(targetCategory);
			setSelectedDocuments(documentsData[targetCategory] || []);
			const nextParams = new URLSearchParams(searchParams);
			nextParams.set('category', targetCategory);
			setSearchParams(nextParams, { replace: true });
			return;
		}

		const openKey = `${matchedDocument.document_id || matchedDocument.name}-${targetCategory}`;
		if (notificationOpenRef.current === openKey) return;

		notificationOpenRef.current = openKey;
		setViewDoc(matchedDocument);
		setViewOpen(true);
	}, [
		loading,
		documentsData,
		documentIdParam,
		documentNameParam,
		searchParams,
		selectedCategory,
		setSearchParams,
	]);

	const columns = [
		{ field: "name", headerName: t('TABLE_HEADERS.DOCUMENT_NAME'), flex: 1 },
		{
			field: "description",
			headerName: t('TABLE_HEADERS.DESCRIPTION'),
			flex: 2,
			renderCell: (params) => {
				// Remove HTML tags and trim whitespace
				const plainText = (params.value || '')
					.replace(/<[^>]+>/g, '')
					.trim();

				return (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							height: '100%',
							width: '100%',
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								lineHeight: '1.3em',
								maxHeight: '2.6em',
								width: '100%',
								color:
									plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1')
										? 'text.secondary'
										: 'inherit',
								wordBreak: 'break-word',
							}}
						>
							{plainText || ''}
						</Box>
					</Box>
				);
			},
			cellClassName: (params) => {
				const plainText = (params.value || '').replace(/<[^>]+>/g, '').trim();
				return plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1') ? "gray-text" : "";
			},
		},
		{
			field: "assigned_machines",
			headerName: t('TABLE_HEADERS.ASSIGNED_MACHINE'),
			flex: 2,
			valueGetter: (_value, row) => {
				const assignedMachines = row?.assigned_machines;
				if (Array.isArray(assignedMachines) && assignedMachines.length > 0) {
					const names = assignedMachines
						.map((m) => m?.machine_name || m?.machine_number || m?.machine_id)
						.filter(Boolean)
						.join(", ");
					return names || t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
				}
				return t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
			},
			cellClassName: (params) => {
				return params.value === t('UI_TEXT.NO_MACHINES_ASSIGNED_1') ? "gray-text" : "";
			},
		},
		{
			field: "actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 140,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params) => {
				const { row } = params;
				const hasUrlAttachment = row?.attachment_type === 'url' && row?.attachment;
				const hasFileAttachment = row?.attachment_type === 'file' && row?.attachment;

				const handleViewDocument = (event) => {
					event.stopPropagation();
					setViewDoc(row);
					setViewOpen(true);
				};

				const handleOpenUrl = (event) => {
					event.stopPropagation();
					checkAndOpenUrl(row?.attachment);
				};

				const handleDownload = async (event) => {
					event.stopPropagation();
					try {
						const response = await downloadDocument(row.path.split("/").pop());
						const blob = response.data;
						if (!(blob instanceof Blob)) {
							throw new Error("Invalid file response");
						}
						const filename = row.path.split("/").pop(); // 👈 key line
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement("a");
						link.href = url;
						link.download = filename;
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
						window.URL.revokeObjectURL(url);
					} catch (error) {
						Swal.fire({
							icon: "error",
							title: t("ALERT_MESSAGES.ERROR"),
							text: t("ALERT_MESSAGES.FAILED_TO_DOWNLOAD_FILE_PLEASE_TRY_AGAING"),
						});
					}
				};

				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>

						<Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center' }}>
							<Tooltip title={t("UI_TEXT.TOOLTIP_VIEW_DOCUMENT")}>
								<IconButton color="primary" onClick={handleViewDocument}>
									<VisibilityIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							{hasUrlAttachment && (
								<Tooltip title={t("UI_TEXT.TOOLTIP_OPEN_URL")}>
									<IconButton color="secondary" onClick={handleOpenUrl}>
										<LaunchIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
							{hasFileAttachment && (
								<Tooltip title={t("UI_TEXT.TOOLTIP_DOWNLOAD_FILE")}>
									<IconButton color="secondary" onClick={handleDownload}>
										<DownloadIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					</Box>
				);
			},
		},
	];

	if (loading) {
		return <ListPageSkeleton showAction={false} />;
	}

	return (
		<Box
			sx={{
				padding: "5px 40px 40px 40px",
				display: "flex",
				flexDirection: "column",
				justifyContent: "flex-start",
				width: "100%",
				minHeight: "500px",
				height: '100%',
			}}>
			<PageTitle
				title={
					selectedMachineId && selectedMachine
						? `Documents:  ${selectedMachine.machine_number} | ${selectedMachine.machine_name}`
						: t('UI_TEXT.DOCUMENT')
				}
			/>

			<Box
				sx={{
					display: "flex",
					width: "100%",
					marginTop: "20px",
					height: "100%",
					minHeight: '500px'
				}}>
				<Box
					sx={{
						width: { xs: "30%", sm: "15%" },
						paddingRight: "20px",
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-start",
						gap: "10px",
					}}>
					<ContextFilterAndSort
						showSearch
						showSort={false}
						searchValue={searchTerm}
						onSearchChange={handleSearchChange}
					/>
					<List sx={{ width: "100%", padding: 0 }}>
						{visibleCategories.map((category) => {
							const categoryDocuments = documentsData[category.name];
							const filteredCategoryDocuments = (categoryDocuments || []).filter((doc) => matchesDocument(doc));
							return (
								<ListItem
									button
									key={category.name}
									ref={category.name === selectedCategory ? selectedCategoryRef : null}
									onClick={() => handleCategoryClick(category.name)}
									selected={category.name === selectedCategory}
									sx={{
										padding: "12px 20px",
										my: 0.5,
										borderRadius: "6px",
										"&:hover": {
											backgroundColor: "#f0f0f0",
											transition: "background-color 0.3s ease, color 0.3s ease",
										},
										backgroundColor: category.name === selectedCategory ? "#e3f2fd" : "transparent",
										color: category.name === selectedCategory ? "#1976d2" : "#000",
										transition: "background-color 0.3s ease, color 0.3s ease",
										cursor: "pointer",
									}}>
									<ListItemText
										primary={
											<>
												{category.name}
												<span
													style={{
														fontSize: "0.8em",
														marginLeft: "8px",
														color: "#888",
													}}>
													({filteredCategoryDocuments?.length})
												</span>
											</>
										}
										sx={{
											color: category.name === selectedCategory ? "#1976d2" : "inherit",
											fontWeight: category.name === selectedCategory ? "bold" : "normal",
										}}
									/>
								</ListItem>
							);
						})}
					</List>
				</Box>
				<Divider orientation="vertical" flexItem sx={{ mx: "10px" }} />
				<Box sx={{ width: "85%", paddingLeft: "20px" }}>
					{machine_id && (
						<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
							<Button
								variant="outlined"
								onClick={() => navigate("/documentation")}
							>
								{t("BUTTON.SHOW_ALL_DOCUMENTS") || "Show All Documents"}
							</Button>
						</Box>
					)}
					{filteredDocuments?.length > 0 && (
						<>
					<Typography variant="h6">
						{categories.find((cat) => cat.name === selectedCategory)?.name}
					</Typography>
					<Typography variant="body1" sx={{ marginBottom: 1 }} color="text.secondary">
								<b>{t('UI_TEXT.DOCUMENTS_1')}</b> {filteredDocuments?.length || 0}
					</Typography>
						</>
					)}

					{filteredDocuments.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "80%",
								width: "100%",
							}}>
							<Alert severity="info">
								{selectedMachineId && selectedMachine
									? t('ALERT_MESSAGES.NO_DOCUMENTS_ASSIGENED')
									: t('ALERT_MESSAGES.NO_DOCUMENT_IN_CATEGORY')}
							</Alert>
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: "16px",
								justifyContent: "flex-start",
							}}>
							<Datagrid
								data={filteredDocuments}
								columns={columns}
								rowIdField="row_id"
								pageSize={10}
								showSearchBar={true}
								rowClick={true}
								onRowClick={(params) => {
									setViewDoc(params.row);
									setViewOpen(true);
								}}
							/>
						</Box>
					)}
				</Box>
			</Box>

			<DocumentViewModal
				open={viewOpen}
				setOpen={setViewOpen}
				document={viewDoc}
				onClose={() => {
					setViewDoc(null);
					setViewOpen(false);
				}}
			/>
		</Box>
	);
};

export default Documents;
